import {
  Box,
  Button,
  Checkbox,
  CollapsibleCard,
  Dialog,
  Flex,
  SvgIcon,
  Text,
  TextInput,
  useToastManager
} from '@volue/wave-react';
import { JsonEditor } from 'json-edit-react';
import React from 'react';

import { create } from 'jsondiffpatch';
import { format } from 'jsondiffpatch/formatters/html';
import 'jsondiffpatch/formatters/styles/html.css';

import { useAppState } from '@/overmind';

import { CenteredLoadingPage } from './CenteredLoadingPage';

const CONFIG_TYPES = [
  'unMapping',
  'switchCharacteristics',
  'switchStandardValues',
  'acLineSegmentStandardValues',
  'powerTransformerNormalizeValues',
  'arcGis',
  'webApp',
  'externalInputs'
] as const;
type ConfigType = typeof CONFIG_TYPES[number];

const CONFIG_TYPE_LABELS: Record<ConfigType, string> = {
  unMapping: 'UN Mapping',
  switchCharacteristics: 'Switch Characteristics',
  switchStandardValues: 'Switch Standard Values',
  acLineSegmentStandardValues: 'AC Line Segment Standard Values',
  powerTransformerNormalizeValues: 'Power Transformer Normalize Values',
  arcGis: 'ArcGIS Tenant Configuration',
  webApp: 'Web App (ArcGIS Web) Configuration',
  externalInputs: 'External Inputs Configuration'
};

type ConfigTypeState =
  | { type: 'LOADING' }
  | { type: 'NOT_FOUND' }
  | { type: 'ERROR' }
  | { type: 'SUCCESS'; data: unknown; edited: unknown };

type ConfigState = Record<ConfigType, ConfigTypeState>;

type AuditLogEntry = {
  id: string;
  changedAt: string;
  changedBy: string;
  newValue: unknown;
};

type HistoryState =
  | { type: 'IDLE' }
  | { type: 'LOADING' }
  | { type: 'ERROR' }
  | { type: 'LOADED'; entries: AuditLogEntry[] };

type HistoryStates = Record<ConfigType, HistoryState>;

type DiffSelection = Record<ConfigType, [string | null, string | null]>;

type DiffModalState =
  | { type: 'CLOSED' }
  | { type: 'OPEN'; entryA: AuditLogEntry; entryB: AuditLogEntry };

type PreSaveDiffModal =
  | { type: 'CLOSED' }
  | { type: 'OPEN'; configType: ConfigType };

const differ = create();

const useCopyToClipboard = () => {
  const copyToClipboard = (text: string) => {
    return navigator.clipboard.writeText(text).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };
  return { copyToClipboard };
};

const initialConfigState: ConfigState = CONFIG_TYPES.reduce((acc, type) => {
  acc[type] = { type: 'LOADING' };
  return acc;
}, {} as ConfigState);

const initialHistoryState: HistoryStates = CONFIG_TYPES.reduce((acc, t) => {
  acc[t] = { type: 'IDLE' };
  return acc;
}, {} as HistoryStates);

const initialDiffSelection: DiffSelection = CONFIG_TYPES.reduce((acc, t) => {
  acc[t] = [null, null];
  return acc;
}, {} as DiffSelection);

export const ConfigurationV2 = () => {
  const toastManager = useToastManager();
  const auth = useAppState().auth;
  const tenant = useAppState().tenants.currentTenant;
  const appConfig = useAppState().config;
  const { copyToClipboard } = useCopyToClipboard();

  const [configState, setConfigState] =
    React.useState<ConfigState>(initialConfigState);
  const [historyState, setHistoryState] =
    React.useState<HistoryStates>(initialHistoryState);
  const [diffSelection, setDiffSelection] =
    React.useState<DiffSelection>(initialDiffSelection);
  const [diffModal, setDiffModal] =
    React.useState<DiffModalState>({ type: 'CLOSED' });
  const [preSaveDiffModal, setPreSaveDiffModal] =
    React.useState<PreSaveDiffModal>({ type: 'CLOSED' });
  const [tenantIdOverride, setTenantIdOverride] = React.useState('');

  const isAuthenticated =
    auth.current === 'AUTHENTICATED' ||
    auth.current === 'AWAITING_ARCGIS_AUTHENTICATION' ||
    auth.current === 'AUTHENTICATING_ARCGIS';
  const token = isAuthenticated ? (auth as any).user?.token ?? '' : '';
  const currentTenantId = tenant.type !== 'NO_TENANT_SELECTED' ? tenant.context.tenantId : '';
  const tenantId = tenantIdOverride || currentTenantId;
  const configApiUrl = appConfig.type !== 'NOT_AVAILABLE' ? appConfig.config.api.configApiUrl : '';

  React.useEffect(() => {
    setConfigState(initialConfigState);
    setHistoryState(initialHistoryState);
    setDiffSelection(initialDiffSelection);
    if (!token || !tenantId || !configApiUrl) return;
    const fetchConfig = async (configType: ConfigType) => {
      try {
        const response = await fetch(
          `${configApiUrl}api/v1/${tenantId}/configurations/${configType}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (response.status === 404) {
          return { configType, state: { type: 'NOT_FOUND' } as ConfigTypeState };
        }
        if (response.ok) {
          const data = await response.json();
          return {
            configType,
            state: { type: 'SUCCESS', data, edited: data } as ConfigTypeState
          };
        }
        return { configType, state: { type: 'ERROR' } as ConfigTypeState };
      } catch {
        return { configType, state: { type: 'ERROR' } as ConfigTypeState };
      }
    };

    Promise.allSettled(CONFIG_TYPES.map(fetchConfig)).then(results => {
      const updates: Partial<ConfigState> = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          updates[result.value.configType] = result.value.state;
        }
      }
      setConfigState(prev => ({ ...prev, ...updates }));
    });
  }, [token, tenantId, configApiUrl]);

  if (appConfig.type === 'NOT_AVAILABLE') return null;
  if (tenant.type === 'NO_TENANT_SELECTED') return null;
  if (
    auth.current !== 'AUTHENTICATED' &&
    auth.current !== 'AWAITING_ARCGIS_AUTHENTICATION' &&
    auth.current !== 'AUTHENTICATING_ARCGIS'
  )
    return null;

  const updateEdited = (configType: ConfigType, json: unknown) => {
    setConfigState(prev => {
      const current = prev[configType];
      if (current.type !== 'SUCCESS' && current.type !== 'NOT_FOUND') return prev;
      return {
        ...prev,
        [configType]: { ...current, type: 'SUCCESS', data: current.type === 'SUCCESS' ? current.data : {}, edited: json }
      };
    });
  };

  const handleSave = (configType: ConfigType) => () => {
    const current = configState[configType];
    if (current.type !== 'SUCCESS' && current.type !== 'NOT_FOUND') return;
    const editedData = current.type === 'SUCCESS' ? current.edited : {};

    fetch(`${configApiUrl}api/v1/${tenantId}/configurations/${configType}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(editedData)
    }).then(response => {
      if (response.ok) {
        toastManager.addToast({
          message: `${CONFIG_TYPE_LABELS[configType]} saved successfully`,
          type: 'success'
        });
        setConfigState(prev => {
          const c = prev[configType];
          if (c.type === 'SUCCESS') {
            return { ...prev, [configType]: { ...c, data: editedData } };
          }
          return {
            ...prev,
            [configType]: { type: 'SUCCESS', data: editedData, edited: editedData }
          };
        });
        fetchHistory(configType);
      } else {
        toastManager.addToast({
          message: `Failed to save ${CONFIG_TYPE_LABELS[configType]}: ${response.statusText}`,
          type: 'error'
        });
      }
    });
  };

  const fetchHistory = (configType: ConfigType) => {
    setHistoryState(prev => ({ ...prev, [configType]: { type: 'LOADING' } }));
    fetch(`${configApiUrl}api/v1/${tenantId}/configurations/${configType}/history`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((entries: AuditLogEntry[]) =>
        setHistoryState(prev => ({ ...prev, [configType]: { type: 'LOADED', entries } }))
      )
      .catch(() =>
        setHistoryState(prev => ({ ...prev, [configType]: { type: 'ERROR' } }))
      );
  };

  const handleHistoryExpand = (configType: ConfigType) => {
    if (historyState[configType].type !== 'IDLE') return;
    fetchHistory(configType);
  };

  const handleRevert = (configType: ConfigType, entry: AuditLogEntry) => {
    if (!entry.newValue) return;

    fetch(`${configApiUrl}api/v1/${tenantId}/configurations/${configType}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(entry.newValue)
    }).then(response => {
      if (response.ok) {
        setConfigState(prev => ({
          ...prev,
          [configType]: { type: 'SUCCESS', data: entry.newValue, edited: entry.newValue }
        }));
        setHistoryState(prev => ({ ...prev, [configType]: { type: 'IDLE' } }));
        setDiffSelection(prev => ({ ...prev, [configType]: [null, null] }));
        toastManager.addToast({
          message: `${CONFIG_TYPE_LABELS[configType]} reverted successfully`,
          type: 'success'
        });
        handleHistoryExpand(configType);
      } else {
        toastManager.addToast({
          message: `Failed to revert ${CONFIG_TYPE_LABELS[configType]}`,
          type: 'error'
        });
      }
    });
  };

  const handleSelectEntry = (configType: ConfigType, entryId: string) => {
    setDiffSelection(prev => {
      const [a, b] = prev[configType];
      if (a === entryId) return { ...prev, [configType]: [b, null] };
      if (b === entryId) return { ...prev, [configType]: [a, null] };
      if (a === null) return { ...prev, [configType]: [entryId, null] };
      if (b === null) return { ...prev, [configType]: [a, entryId] };
      return { ...prev, [configType]: [b, entryId] };
    });
  };

  const allLoading = CONFIG_TYPES.every(t => configState[t].type === 'LOADING');

  return (
    <Flex flow="column" css={{ height: '100%' }}>
      <Box
        css={{
          fontSize: '$alpha',
          color: '$colorAccent70',
          paddingLeft: '$spacingXl',
          paddingTop: '$spacingXl'
        }}
      >
        Grid Calculator configurations (config-api)
      </Box>
      <Box
        css={{
          fontSize: '$gamme',
          color: '$colorAccent40',
          paddingLeft: '$spacingXl',
          paddingTop: '$spacingS',
          fontStyle: 'italic'
        }}
      >
        Network Service configuration is not editable here. Update it in Azure Key Vault
        (secret <Box as="code" css={{ fontFamily: 'monospace' }}>NetworkService-Tenant-Configuration-{`{tenantId}`}</Box>);
        the change requires a Network Service restart to take effect.
      </Box>
      <Flex
        css={{
          fontSize: '$gamme',
          color: '$colorAccent40',
          paddingLeft: '$spacingXl',
          paddingTop: '$spacingS',
          alignItems: 'center'
        }}
      >
        <span>Tenant Id:</span>
        <Flex css={{ alignItems: 'center' }}>
          <Box as="span" css={{ fontWeight: 'bolder', marginLeft: '$spacingXs' }}>
            {tenantId}
          </Box>
          <Box
            variant="ghost"
            as={Button}
            css={{
              fontSize: '$gamme',
              color: '$colorAccent40'
            }}
          >
            <SvgIcon
              iconName="copy"
              onClick={() =>
                copyToClipboard(tenantId)
                  .then(() => {
                    toastManager.addToast({
                      message: `Tenant ID (${tenantId}) copied to clipboard`,
                      type: 'success',
                      duration: 2000
                    });
                  })
                  .catch(() => {
                    toastManager.addToast({
                      message: `Failed to copy Tenant ID (${tenantId}) to clipboard`,
                      type: 'error',
                      duration: 2000
                    });
                  })
              }
            />
          </Box>
        </Flex>
      </Flex>
      <Flex
        css={{
          paddingLeft: '$spacingXl',
          paddingTop: '$spacingS',
          alignItems: 'center',
          gap: '$spacingS'
        }}
      >
        <TextInput
          placeholder="Paste tenant ID to override"
          value={tenantIdOverride}
          onChange={e => setTenantIdOverride(e.target.value.trim())}
          css={{ maxWidth: '24rem' }}
        />
        {tenantIdOverride && (
          <Button
            variant="outline"
            size="small"
            onClick={() => setTenantIdOverride('')}
          >
            Reset
          </Button>
        )}
      </Flex>
      {!tenantIdOverride && (
        <Box
          css={{
            fontSize: '$gamme',
            color: '$colorAccent40',
            paddingLeft: '$spacingXl',
            paddingTop: '$spacingS'
          }}
        >
          Tenant Name: {tenant.context.name}
        </Box>
      )}
      {tenantIdOverride && (
        <Box
          css={{
            fontSize: '$gamme',
            color: '$colorWarning50',
            paddingLeft: '$spacingXl',
            paddingTop: '$spacingS'
          }}
        >
          Using overridden tenant ID (not the selected tenant)
        </Box>
      )}
      {allLoading && <CenteredLoadingPage text="Loading configurations..." />}
      {!allLoading && (
        <Flex
          flow="column"
          css={{ padding: '$spacingXxxl', paddingTop: '$spacingM' }}
        >
          {CONFIG_TYPES.map(configType => {
            const cardState = configState[configType];
            return (
              <CollapsibleCard key={configType}>
                <CollapsibleCard.Header>
                  <Box>{CONFIG_TYPE_LABELS[configType]}</Box>
                </CollapsibleCard.Header>
                <CollapsibleCard.Body>
                  <Flex css={{ overflowY: 'auto' }}>
                    <Box css={{ maxHeight: '70vh', width: '100%' }}>
                      {cardState.type === 'LOADING' && (
                        <CenteredLoadingPage text="Loading..." />
                      )}
                      {cardState.type === 'ERROR' && (
                        <Text>Failed to load configuration.</Text>
                      )}
                      {cardState.type === 'NOT_FOUND' && (
                        <>
                          <Text>No configuration found. Use Save to create.</Text>
                          <JsonEditor
                            collapse
                            data={{}}
                            setData={json => updateEdited(configType, json)}
                          />
                          <Box css={{ paddingTop: '$spacingM' }}>
                            <Button onClick={handleSave(configType)}>Save</Button>
                          </Box>
                        </>
                      )}
                      {cardState.type === 'SUCCESS' && (
                        <>
                          <JsonEditor
                            collapse
                            data={cardState.edited as object}
                            setData={json => updateEdited(configType, json)}
                          />
                          <Box css={{ paddingTop: '$spacingM' }}>
                            <Flex gap="spacingS">
                              <Button onClick={handleSave(configType)}>Save</Button>
                              <Button
                                variant="outline"
                                onClick={() => setPreSaveDiffModal({ type: 'OPEN', configType })}
                              >
                                Preview Changes
                              </Button>
                            </Flex>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Flex>

                  <Box css={{ borderTop: '1px solid $colorAccent20', marginTop: '$spacingM', paddingTop: '$spacingM' }}>
                    <CollapsibleCard
                      onIsOpenChange={isOpen => {
                        if (isOpen) handleHistoryExpand(configType);
                      }}
                    >
                      <CollapsibleCard.Header>
                        <Box>History</Box>
                      </CollapsibleCard.Header>
                      <CollapsibleCard.Body>
                        {historyState[configType].type === 'LOADING' && (
                          <CenteredLoadingPage text="Loading history..." />
                        )}
                        {historyState[configType].type === 'ERROR' && (
                          <Text>Failed to load history.</Text>
                        )}
                        {historyState[configType].type === 'IDLE' && null}
                        {historyState[configType].type === 'LOADED' &&
                          (historyState[configType] as { type: 'LOADED'; entries: AuditLogEntry[] }).entries.length === 0 && (
                            <Text>No history available.</Text>
                          )}
                        {historyState[configType].type === 'LOADED' && (() => {
                          const loaded = historyState[configType] as { type: 'LOADED'; entries: AuditLogEntry[] };
                          const [idA, idB] = diffSelection[configType];
                          const canCompare = idA !== null && idB !== null;
                          return (
                            <>
                              <Box css={{ paddingBottom: '$spacingS' }}>
                                <Button
                                  size="small"
                                  variant="outline"
                                  disabled={!canCompare}
                                  onClick={() => {
                                    const a = loaded.entries.find(e => e.id === idA)!;
                                    const b = loaded.entries.find(e => e.id === idB)!;
                                    setDiffModal({ type: 'OPEN', entryA: a, entryB: b });
                                  }}
                                >
                                  Compare
                                </Button>
                              </Box>
                              {loaded.entries.map(entry => (
                                <Flex key={entry.id} gap="spacingS" css={{ paddingBottom: '$spacingS', alignItems: 'center' }}>
                                  <Checkbox
                                    isDisabled={entry.newValue === null}
                                    isChecked={diffSelection[configType][0] === entry.id || diffSelection[configType][1] === entry.id}
                                    onChange={() => handleSelectEntry(configType, entry.id)}
                                  >
                                    <Checkbox.Indicator />
                                  </Checkbox>
                                  <Text css={{ flexGrow: 1 }}>
                                    {new Date(entry.changedAt).toLocaleString()} by {entry.changedBy}
                                  </Text>
                                  {entry.newValue !== null && entry.newValue !== undefined && (
                                    <Dialog>
                                      <Dialog.Trigger as={Button} size="small" variant="outline">
                                        Revert
                                      </Dialog.Trigger>
                                      <Dialog.Box width="28rem">
                                        <Dialog.Header>
                                          <Dialog.Title variant="delta">Confirm revert</Dialog.Title>
                                          <Dialog.Close as={Button} variant="ghost" shape="circle" size="small" withLoneIcon aria-label="Close">
                                            <SvgIcon iconName="close" size="small" />
                                          </Dialog.Close>
                                        </Dialog.Header>
                                        <Dialog.Body>
                                          <Text>
                                            Revert to version from {new Date(entry.changedAt).toLocaleString()} by {entry.changedBy}?
                                          </Text>
                                          <Flex gap="spacingS" css={{ paddingTop: '$spacingM' }}>
                                            <Dialog.Close as={Button} variant="outline">Cancel</Dialog.Close>
                                            <Button onClick={() => handleRevert(configType, entry)}>Confirm</Button>
                                          </Flex>
                                        </Dialog.Body>
                                      </Dialog.Box>
                                    </Dialog>
                                  )}
                                </Flex>
                              ))}
                            </>
                          );
                        })()}
                      </CollapsibleCard.Body>
                    </CollapsibleCard>
                  </Box>
                </CollapsibleCard.Body>
              </CollapsibleCard>
            );
          })}
        </Flex>
      )}
      {diffModal.type === 'OPEN' && (() => {
        const delta = differ.diff(diffModal.entryA.newValue, diffModal.entryB.newValue);
        const html = delta ? format(delta, diffModal.entryA.newValue) : null;
        return (
          <Dialog
            isOpen
            onIsOpenChange={(open: boolean) => { if (!open) setDiffModal({ type: 'CLOSED' }); }}
          >
            <Dialog.Box width="60rem">
              <Dialog.Header>
                <Dialog.Title variant="delta">
                  {`Diff: ${new Date(diffModal.entryA.changedAt).toLocaleString()} vs ${new Date(diffModal.entryB.changedAt).toLocaleString()}`}
                </Dialog.Title>
                <Dialog.Close as={Button} variant="ghost" shape="circle" size="small" withLoneIcon aria-label="Close">
                  <SvgIcon iconName="close" size="small" />
                </Dialog.Close>
              </Dialog.Header>
              <Dialog.Body>
                {html
                  ? <div dangerouslySetInnerHTML={{ __html: html }} />
                  : <Text>No differences between these two versions.</Text>
                }
              </Dialog.Body>
            </Dialog.Box>
          </Dialog>
        );
      })()}
      {preSaveDiffModal.type === 'OPEN' && (() => {
        const cs = configState[preSaveDiffModal.configType];
        if (cs.type !== 'SUCCESS') return null;
        const saved = cs.data;
        const current = cs.edited;
        const delta = differ.diff(saved, current);
        const html = delta ? format(delta, saved) : null;
        return (
          <Dialog
            isOpen
            onIsOpenChange={(open: boolean) => {
              if (!open) setPreSaveDiffModal({ type: 'CLOSED' });
            }}
          >
            <Dialog.Box width="60rem">
              <Dialog.Header>
                <Dialog.Title variant="delta">Preview Changes</Dialog.Title>
                <Dialog.Close
                  as={Button}
                  variant="ghost"
                  shape="circle"
                  size="small"
                  withLoneIcon
                  aria-label="Close"
                >
                  <SvgIcon iconName="close" size="small" />
                </Dialog.Close>
              </Dialog.Header>
              <Dialog.Body>
                {html
                  ? <div dangerouslySetInnerHTML={{ __html: html }} />
                  : <Text>No changes to save.</Text>
                }
              </Dialog.Body>
            </Dialog.Box>
          </Dialog>
        );
      })()}
    </Flex>
  );
};
